import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

/**
 * Shared trend-graph vocabulary — one definition for every "View trend" chart
 * (Anthropometric, Chief complaints, Diagnosis, Lab investigations), so the
 * four can't drift apart again. Previously each drew its own colour
 * (#6366f1 / #4c1d95) and its own dot radius (3.5 / 4).
 *
 * Per Figma 4090:15075 ("Dot · Mild · Nov") the marker is a 20×20 box:
 *   • Core — circle r 5, solid #6A5EF5
 *   • Ring — circle r 9.3, stroke #6A5EF5 at 50% opacity, width 1.4, no fill
 *   • a 3.6px transparent gap between the two (so it is NOT a box-shadow ring)
 * No halo, no drop shadow, and only ONE state: every dot on the board — Mild,
 * Severe, Moderate, dip, Resolved — exports to the same asset. The severity
 * words are point labels, not variants.
 */

/** Line + dot colour for every trend chart. */
export const TREND_COLOR = "#6a5ef5";

/** Board ring: same hue at half alpha. */
export const TREND_RING = "rgba(106, 94, 245, 0.5)";

export const TREND_CORE_R = 5;
export const TREND_RING_R = 9.3;
export const TREND_RING_W = 1.4;

/**
 * One data-point marker. `children` is passed through so callers can keep
 * their <title> tooltip on the hit target; `onMouseEnter` / `onMouseLeave`
 * lift the hover state up to a chart-level HTML tooltip (see `useTrendTip`)
 * for the white-background reveal.
 */
export const TrendDot = ({ cx, cy, scale, children, onMouseEnter, onMouseLeave, onMouseMove }) => {
  const k = scale || 1;
  return (
    <g
      className="trend-dot"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onMouseMove={onMouseMove}
    >
      <circle
        cx={cx}
        cy={cy}
        r={TREND_RING_R * k}
        fill="none"
        stroke={TREND_RING}
        strokeWidth={TREND_RING_W * k}
      />
      <circle cx={cx} cy={cy} r={TREND_CORE_R * k} fill={TREND_COLOR}>
        {children}
      </circle>
    </g>
  );
};

TrendDot.propTypes = {
  cx: PropTypes.number.isRequired,
  cy: PropTypes.number.isRequired,
  /** Uniform scale for charts drawn smaller than the board's 20px marker. */
  scale: PropTypes.number,
  children: PropTypes.node,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onMouseMove: PropTypes.func,
};

/**
 * Hook that gives a chart a white HTML hover-tooltip in place of the SVG
 * `<title>` browser tooltip (which every OS paints its own way, and can't
 * be styled). Usage:
 *
 *   const { wrapRef, tip, showTip, hideTip } = useTrendTip();
 *   return (
 *     <div className="cc-chart" ref={wrapRef}>
 *       <svg>...
 *         <TrendDot onMouseEnter={(e) => showTip(e, "08 Jan · Mild")} onMouseLeave={hideTip} />
 *       </svg>
 *       <TrendTip tip={tip} />
 *     </div>
 *   );
 *
 * Position is captured off the pointer event and reported in wrapper-local
 * pixels, so the caller doesn't need to know its SVG viewBox math.
 */
export const useTrendTip = () => {
  const wrapRef = useRef(null);
  const [tip, setTip] = useState(null);
  const showTip = useCallback((e, text) => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    setTip({ left: e.clientX - rect.left, top: e.clientY - rect.top, text });
  }, []);
  const hideTip = useCallback(() => setTip(null), []);
  return { wrapRef, tip, showTip, hideTip };
};

/**
 * Small white pill anchored near the pointer. Reads from the state returned
 * by `useTrendTip`; renders nothing when `tip` is null.
 *
 * Positioning happens in a `useLayoutEffect` AFTER the tip has laid out —
 * we need its measured width/height to clamp against the wrapper edges and
 * to decide whether the tip fits above the pointer or has to flip below.
 * The tip is rendered `visibility:hidden` on the first frame so the initial
 * unmeasured coordinates never flash.
 */
export const TrendTip = ({ tip }) => {
  const popRef = useRef(null);
  // `visible: false` on the first frame of a new hover so the pre-measured
  // position never paints; the layout effect flips it true after clamping.
  const [pos, setPos] = useState({ left: 0, top: 0, visible: false });

  useLayoutEffect(() => {
    const pop = popRef.current;
    if (!tip || !pop) return;
    // `.cc-chart` carries `position: relative`, so it becomes our
    // offsetParent — its content box is the clamp region for the tip.
    // Fall back to the viewport if the parent is somehow missing (unmounted
    // between event + effect, or someone dropped the relative rule).
    const parent = pop.offsetParent;
    const box = parent
      ? { w: parent.clientWidth, h: parent.clientHeight }
      : { w: window.innerWidth, h: window.innerHeight };
    const popRect = pop.getBoundingClientRect();
    const PAD = 6;         // gap from the wrapper edges
    const ABOVE_GAP = 10;  // gap between tip's bottom edge and the dot
    const BELOW_GAP = 20;  // gap between the dot and the tip's top edge

    // Horizontal — centre on the pointer, then clamp so the tip stays
    // inside the wrapper's content box. Handles both left-edge dots
    // (would render off-screen left) and right-edge dots (off-screen right).
    let left = tip.left - popRect.width / 2;
    left = Math.max(PAD, Math.min(box.w - popRect.width - PAD, left));

    // Vertical — prefer above the pointer; flip below when there isn't
    // enough headroom (top row of a tall chart, or a chart whose plotting
    // area starts near the top of the wrapper).
    let top = tip.top - popRect.height - ABOVE_GAP;
    if (top < PAD) top = tip.top + BELOW_GAP;

    setPos({ left, top, visible: true });
  }, [tip]);

  if (!tip) return null;
  return (
    <div
      className="trend-tt"
      ref={popRef}
      role="tooltip"
      style={{
        left: `${pos.left}px`,
        top: `${pos.top}px`,
        visibility: pos.visible ? "visible" : "hidden",
      }}
    >
      {tip.text}
    </div>
  );
};
TrendTip.propTypes = {
  tip: PropTypes.shape({
    left: PropTypes.number,
    top: PropTypes.number,
    text: PropTypes.node,
  }),
};

export default TrendDot;
